import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, User, Calendar, LogOut, Ticket, LayoutDashboard, Briefcase, Menu, X } from 'lucide-react';

import Logo from './Logo';
import LangSelector from './LangSelector';
import AuthModal from './auth/AuthModal';
import { useAuth } from '../context/AuthContext';
import { tt } from '../lib/empresasTxt';

// En la home (/) los enlaces de sección son anchors (#clases, #bonos, ...).
// En otras páginas, redirigen a la home con el hash.
export default function Nav() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();

  const enHome = pathname === '/';
  const seccion = (hash) => (enHome ? `#${hash}` : `/#${hash}`);

  const [authModalTab, setAuthModalTab] = useState(null); // null | 'login' | 'signup'
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [movilAbierto, setMovilAbierto] = useState(false);
  const menuRef = useRef(null);

  // Cierra el menú móvil al cambiar de página
  useEffect(() => { setMovilAbierto(false); }, [pathname]);

  // Cerrar dropdown al click fuera o Escape
  useEffect(() => {
    if (!menuAbierto) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuAbierto(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenuAbierto(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuAbierto]);

  const inicial = (user?.nombre || user?.email || '?').trim().charAt(0).toUpperCase();
  const saludo = user?.nombre ? user.nombre.split(' ')[0] : (user?.email || '');

  const handleLogout = async () => {
    setMenuAbierto(false);
    await logout();
    if (pathname === '/mis-reservas' || pathname === '/mis-bonos') navigate('/', { replace: true });
  };

  return (
    <>
      <nav className="nav">
        <Logo />
        <div className="nav-links">
          <a href={seccion('clases')}>{t('nav.clases')}</a>
          <a href={seccion('bonos')}>{t('nav.bonos')}</a>
          <a href={seccion('profesores')}>{t('nav.profesores')}</a>
          <Link to="/horarios">{t('nav.horarios')}</Link>
          <Link to="/eventos">{t('nav.eventos')}</Link>
          <Link to="/videos">{t('nav.videos')}</Link>
          <Link to="/regalo">{t('nav.regalo')}</Link>
        </div>
        <div className="nav-right">
          <LangSelector />
          <button
            type="button"
            className="nav-burger"
            onClick={() => setMovilAbierto((v) => !v)}
            aria-label="Menú"
            aria-expanded={movilAbierto}
          >
            {movilAbierto ? <X size={20} strokeWidth={1.75} /> : <Menu size={20} strokeWidth={1.75} />}
          </button>

          {isAuthenticated ? (
            <div className="user-menu" ref={menuRef}>
              <button
                type="button"
                className="user-trigger"
                onClick={() => setMenuAbierto((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={menuAbierto}
              >
                <span className="user-avatar" aria-hidden="true">{inicial}</span>
                <span className="user-saludo">{saludo}</span>
                <ChevronDown size={14} strokeWidth={1.75} className={'user-chev' + (menuAbierto ? ' is-open' : '')} />
              </button>

              {menuAbierto && (
                <ul className="user-menu-list" role="menu">
                  {user?.rol === 'admin' && (
                    <li>
                      <Link to="/admin" className="user-menu-item" role="menuitem" onClick={() => setMenuAbierto(false)}>
                        <LayoutDashboard size={16} strokeWidth={1.5} />
                        <span>{t('auth.menu.panel_admin')}</span>
                      </Link>
                    </li>
                  )}
                  {user?.rol === 'profesor' && (
                    <li>
                      <Link to="/profesor" className="user-menu-item" role="menuitem" onClick={() => setMenuAbierto(false)}>
                        <Briefcase size={16} strokeWidth={1.5} />
                        <span>{t('auth.menu.panel_profesor')}</span>
                      </Link>
                    </li>
                  )}
                  {(!user?.rol || user.rol === 'alumno') && (
                    <>
                      <li>
                        <Link to="/mis-reservas" className="user-menu-item" role="menuitem" onClick={() => setMenuAbierto(false)}>
                          <Calendar size={16} strokeWidth={1.5} />
                          <span>{t('auth.menu.mis_reservas')}</span>
                        </Link>
                      </li>
                      <li>
                        <Link to="/mis-bonos" className="user-menu-item" role="menuitem" onClick={() => setMenuAbierto(false)}>
                          <Ticket size={16} strokeWidth={1.5} />
                          <span>{t('auth.menu.mis_bonos')}</span>
                        </Link>
                      </li>
                    </>
                  )}
                  <li>
                    <button type="button" className="user-menu-item" role="menuitem" onClick={handleLogout}>
                      <LogOut size={16} strokeWidth={1.5} />
                      <span>{t('auth.menu.cerrar_sesion')}</span>
                    </button>
                  </li>
                </ul>
              )}
            </div>
          ) : (
            <>
              <button
                type="button"
                className="nav-acceder"
                onClick={() => setAuthModalTab('login')}
              >
                <User size={14} strokeWidth={1.75} />
                <span>{t('nav.acceder')}</span>
              </button>
              <Link to="/horarios" className="nav-cta">{t('nav.reservar')}</Link>
            </>
          )}
        </div>
        {movilAbierto && (
          <div className="nav-movil" onClick={() => setMovilAbierto(false)}>
            <a href={seccion('clases')}>{t('nav.clases')}</a>
            <a href={seccion('bonos')}>{t('nav.bonos')}</a>
            <a href={seccion('profesores')}>{t('nav.profesores')}</a>
            <Link to="/horarios">{t('nav.horarios')}</Link>
            <Link to="/eventos">{t('nav.eventos')}</Link>
            <Link to="/videos">{t('nav.videos')}</Link>
            <Link to="/regalo">{t('nav.regalo')}</Link>
            <Link to="/empresas">{tt(lang, 'eyebrow')}</Link>
          </div>
        )}
      </nav>

      {authModalTab && (
        <AuthModal
          initialTab={authModalTab}
          onClose={() => setAuthModalTab(null)}
        />
      )}
    </>
  );
}
